import { startTransition, useEffect, useRef } from "react";

import { useQueryLoader } from "react-relay";

import { completeVariables } from "app/utils/routePreload";

import type { PreloadedRoute } from "app/utils/routePreload";
import type { PreloadedQuery } from "react-relay";
import type {
  FetchPolicy,
  GraphQLTaggedNode,
  OperationType,
  VariablesOf,
} from "relay-runtime";

/**
 * Owns the query reference a page renders from, starting with the one its route
 * loader preloaded.
 *
 * A loader only knows the URL, and stops running once the page is mounted (see
 * the route's `shouldRevalidate`), so everything the page varies its query by
 * afterwards — a selected tab, a value read out of the response — is loaded
 * here instead. Pass the variables the query should be running with right now:
 * whenever they drift from the ones in flight, the query is loaded again inside
 * a transition, which leaves the data already on screen there until the new
 * data arrives.
 *
 * Returns null for the first render of a page whose loader preloaded nothing
 * (see `canPreloadRouteQueries`) — render a skeleton for that render; the load
 * starts immediately after it.
 *
 * @param query - The page's query, as passed to `usePreloadedQuery`.
 * @param preloaded - The route's loader data.
 * @param variables - Variables the query should be running with.
 * @param options - `fetchPolicy` for the loads started here; pass the one the
 *   route's loader uses, so the page fetches the same way however it arrived.
 */
export function useRoutePreloadedQuery<TQuery extends OperationType>(
  query: GraphQLTaggedNode,
  preloaded: PreloadedRoute<TQuery> | null | undefined,
  variables: VariablesOf<TQuery>,
  { fetchPolicy }: { fetchPolicy?: FetchPolicy } = {},
): PreloadedQuery<TQuery> | null {
  // A preloaded reference is good for one mount: whoever mounted first released
  // it on the way out, and a released reference reads from records Relay is
  // free to collect. A page that mounts again on the same loader data — a tab
  // the user left and came back to — loads the query itself instead.
  const usable = preloaded != null && !preloaded.queryRef.isDisposed;
  const preloadedKey = usable ? preloaded.variablesKey : null;

  const [queryRef, loadQuery] = useQueryLoader<TQuery>(
    query,
    usable ? preloaded.queryRef : undefined,
  );

  // What we hand out is checked too, not just what came in: useQueryLoader
  // switches away from a reference by setting state during render, so the
  // render that switches still returns the old one — one render is all it
  // takes for usePreloadedQuery to read collected records and warn.
  const live = queryRef && !queryRef.isDisposed ? queryRef : null;

  // What the live reference is fetching, so a reload only happens on a real
  // change. Tracked here rather than read back off the reference, which Relay
  // may report in a different shape than it was given: a key that never
  // matched would reload the query forever.
  const fetching = useRef({
    preloaded,
    preloadedKey,
    variablesKey: preloadedKey,
  });

  const completed = completeVariables<TQuery>(query, variables);
  const variablesKey = JSON.stringify(completed);

  useEffect(() => {
    if (
      fetching.current.preloaded !== preloaded ||
      fetching.current.preloadedKey !== preloadedKey
    ) {
      // Either the loader ran again, and useQueryLoader has already switched to
      // its reference, or the reference we were counting on was released while
      // we stayed mounted — React destroys a tree's effects while a Suspense
      // boundary hides it, and Relay releases what it holds on the way out. In
      // both cases what's in flight is now whatever the loader last gave us,
      // which is nothing at all if that reference is gone.
      fetching.current = {
        preloaded,
        preloadedKey,
        variablesKey: preloadedKey,
      };
    }

    if (live && fetching.current.variablesKey === variablesKey) {
      return;
    }

    fetching.current = { preloaded, preloadedKey, variablesKey };

    if (!live) {
      // Nothing on screen to hold onto, so no transition: the page is showing
      // a skeleton until this resolves either way.
      loadQuery(completed, { fetchPolicy });
      return;
    }

    startTransition(() => loadQuery(completed, { fetchPolicy }));
  }, [
    preloaded,
    preloadedKey,
    live,
    completed,
    variablesKey,
    loadQuery,
    fetchPolicy,
  ]);

  return live;
}
